import _ from "lodash";
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import classes from "./FilterHeader.scss";
import FilterDropdown from "UIComponents/FilterDropdown";
import FilterDropdownV2 from "UIComponents/FilterDropdownV2";
import { useI18n } from "Hooks";
import { colors } from "Constants";
import { multiLineEllipsis } from "Utils";
import { IconDisclosure, SearchInput, Tooltip } from "@toddle-edu/ds-web";
import { FilterOutlined } from "@toddle-edu/ds-icons";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { isGlobalHeaderOnMultipleScreensEnabled } from "Post/utils";
import VirtualisedFiltersPopup from "Teacher/routes/SchoolLibrary/components/VirtualisedFiltersPopup";

const styles = {
  filterBoxStyle: {
    border: "none",
    padding: "0",
    width: "max-content",
    minWidth: "unset",
  },
  buttonDropDownContainerStyle: {
    top: "40px",
    width: "max-content",
    minWidth: "164px",
    zIndex: "300",
  },
  arrowContainerStyle: {
    width: "12px",
  },
  nameTextStyle: {
    ...multiLineEllipsis({ maxCharacter: 20, lineCount: 1 }),
    color: colors.black,
    fontSize: "1.2rem",
  },
  filterTextStyle: {
    color: colors.neutral400,
    fontSize: "1.2rem",
  },
  checkListContainerStyle: {
    maxWidth: "252px",
  },
};

const FilterHeader = props => {
  const {
    filtersInfo,
    onFilterChange,
    filtersValue,
    isFilterDropdownV2 = false,
    showMoreFilters = false,
    splitFromIndex,
    showSearchCountLabel,
    searchCountLabel,
    searchPlaceholder,
    testId = "course",
    isCourseListLoading,
    updateCourseFilterSearchText,
    hasNextPageForCourseFilter,
    isFetchingMoreDataForCourseFilter,
    loadMoreCourseFilterFeed,
    isClassesListLoading,
    hasNextPageForClasses,
    isFetchingMoreDataForClasses,
    updateClassFilterSearchText,
    loadMoreClassFeed,
    isCreatorListLoading,
    hasNextPageForCreatorFilter,
    isFetchingMoreDataForCreatorFilter,
    updateCreatorFilterSearchText,
    loadMoreCreatorFilterFeed,
    isCombinedFiltersEnabled,
    filterOptions,
    defaultSelectedOptions,
    onFilterChangeCombined,
  } = props;

  const handleCourseFilterScrollEnd = () => {
    if (!isFetchingMoreDataForCourseFilter && hasNextPageForCourseFilter) {
      loadMoreCourseFilterFeed();
    }
  };
  const handleClassFilterScrollEnd = () => {
    if (!isFetchingMoreDataForClasses && hasNextPageForClasses) {
      loadMoreClassFeed();
    }
  };
  const handleCreatorFilterScrollEnd = () => {
    if (!isFetchingMoreDataForCreatorFilter && hasNextPageForCreatorFilter) {
      loadMoreCreatorFilterFeed();
    }
  };
  const splitFiltersFromIndex = splitFromIndex || filtersInfo.length;
  const [toggleMoreFilters, setToggleMoreFilters] = useState(false);

  const searchBarRef = useRef(null);

  const { t } = useI18n(["common", "project"]);
  const searchBarProps = {
    showCancelIcon: false,
    showSearchIcon: false,
    placeholder: t("common:search"),
    shouldAnimate: false,
    customStyle: {
      padding: "4px",
    },
  };

  const onSearchChange = value => {
    onFilterChange({ searchText: value }, ["searchText"]);
  };

  const updateGroupByFilters = params => {
    onFilterChange(params, Object.keys(params));
  };

  const getFilterV2Component = filter => {
    return (
      <ConditionalWrapper
        wrapper={<Tooltip tooltip={filter.toolTipText} placement="top" />}
        condition={filter.isToolTipVisible}
        type={"wrapperAsElement"}
      >
        <div>
          <FilterDropdownV2
            name={filter.name}
            options={filter.options}
            testId={`${testId}-filterHeader`}
            label={filter.label}
            showSearchBar={filter.showSearchBar}
            showAllOption={filter.showAllOption}
            updateInputField={updateGroupByFilters}
            value={_.get(filtersValue, `${filter.name}`, [])}
            noOptionLabel={filter.noOptionLabel}
            type={filter.type}
            showSelectedOptionsOnTop={filter.showSelectedOptionsOnTop}
            isDisabled={filter.isDisabled}
            isLocalizedNoOptionLabel={filter.isLocalizedNoOptionLabel}
            showArchivedOptionsAtLast={filter.showArchivedOptionsAtLast}
            handleCourseFilterScrollEnd={handleCourseFilterScrollEnd}
            handleClassFilterScrollEnd={handleClassFilterScrollEnd}
            handleCreatorFilterScrollEnd={handleCreatorFilterScrollEnd}
            isFetchingMoreDataForCourseFilter={
              isFetchingMoreDataForCourseFilter
            }
            updateCourseFilterSearchText={updateCourseFilterSearchText}
            updateClassFilterSearchText={updateClassFilterSearchText}
            updateCreatorFilterSearchText={updateCreatorFilterSearchText}
            isCourseListLoading={isCourseListLoading}
            isClassesListLoading={isClassesListLoading}
            hasNextPageForClasses={hasNextPageForClasses}
            isFetchingMoreDataForClasses={isFetchingMoreDataForClasses}
            isCreatorListLoading={isCreatorListLoading}
            isFetchingMoreDataForCreatorFilter={
              isFetchingMoreDataForCreatorFilter
            }
          />
        </div>
      </ConditionalWrapper>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.actionContainer}>
        <div className={classes.headerLeftContainer}>
          <div className={classes.searchBarContainer}>
            <SearchInput
              size={"medium"}
              placeholder={searchPlaceholder ?? t("common:search")}
              testId={`${testId}-filterHeader`}
              value={filtersValue?.searchText ?? ""}
              onChange={onSearchChange}
              dsVersion={isGlobalHeaderOnMultipleScreensEnabled ? "2.0" : "1.0"}
              focusSearchBySlash={isGlobalHeaderOnMultipleScreensEnabled}
            />
          </div>

          {showSearchCountLabel && (
            <div className="text-body-1">{searchCountLabel}</div>
          )}
        </div>
        {isCombinedFiltersEnabled && (
          <VirtualisedFiltersPopup
            options={filterOptions}
            defaultSelectedOptions={defaultSelectedOptions}
            onFilterChange={onFilterChangeCombined}
            isFetchingMoreDataForCourseFilter={
              isFetchingMoreDataForCourseFilter
            }
            isCourseListLoading={isCourseListLoading}
            hasNextPageForCourseFilter={hasNextPageForCourseFilter}
            loadMoreCourseFilterFeed={loadMoreCourseFilterFeed}
            isFetchingMoreDataForClasses={isFetchingMoreDataForClasses}
            isClassesListLoading={isClassesListLoading}
            hasNextPageForClasses={hasNextPageForClasses}
            loadMoreClassFeed={loadMoreClassFeed}
            isCreatorListLoading={isCreatorListLoading}
            isFetchingMoreDataForCreatorFilter={
              isFetchingMoreDataForCreatorFilter
            }
            hasNextPageForCreatorFilter={hasNextPageForCreatorFilter}
            loadMoreCreatorFilterFeed={loadMoreCreatorFilterFeed}
          />
        )}
        {!isCombinedFiltersEnabled &&
          (!isFilterDropdownV2 ? (
            <div className={classes.headerRightContainer}>
              {_.map(filtersInfo, filter => (
                <Tooltip tooltip={filter.toolTipText} key={filter.id}>
                  <FilterDropdown
                    name={filter.name}
                    options={filter.options}
                    label={filter.label}
                    showSearchBar={filter.showSearchBar}
                    isCheckList={filter.isCheckList}
                    showAllOption={filter.showAllOption}
                    updateInputField={updateGroupByFilters}
                    value={_.get(filtersValue, `${filter.name}`, [])}
                    isSearchBasedOnQuery={filter.isSearchBasedOnQuery}
                    organizationId={filter.organizationId}
                    archivalState={filter.archivalState}
                    graphqlQueryEnhancer={filter.graphqlQueryEnhancer}
                    noOptionLabel={filter.noOptionLabel}
                    isDisabled={filter.isDisabled}
                    testId={`${testId}-filterHeader-${filter.name}`}
                    //style props
                    filterBoxStyle={styles.filterBoxStyle}
                    buttonDropDownContainerStyle={
                      styles.buttonDropDownContainerStyle
                    }
                    arrowContainerStyle={styles.arrowContainerStyle}
                    nameTextStyle={styles.nameTextStyle}
                    filterTextStyle={styles.filterTextStyle}
                    emptyTextStyle={styles.nameTextStyle}
                    checkListContainerStyle={styles.checkListContainerStyle}
                    searchBarProps={searchBarProps}
                  />
                </Tooltip>
              ))}
            </div>
          ) : (
            <div className={classes.headerRightContainerV2}>
              {_.map(
                filtersInfo
                  .filter(filter => !filter.isHidden)
                  .slice(0, splitFiltersFromIndex),
                getFilterV2Component
              )}

              {showMoreFilters ? (
                <IconDisclosure
                  icon={<FilterOutlined />}
                  testId={`${testId}-filterHeader-moreFilters`}
                  _legacy_variant="outlined-subtle"
                  variant="neutral"
                  type="outlined"
                  onClick={() => setToggleMoreFilters(!toggleMoreFilters)}
                  isActivated={toggleMoreFilters}
                />
              ) : null}
            </div>
          ))}
      </div>
      {toggleMoreFilters ? (
        <div className={classes.moreActionContainer}>
          {_.map(
            filtersInfo
              .filter(filter => !filter.isHidden)
              .slice(splitFiltersFromIndex),
            filter => {
              return getFilterV2Component(filter);
            }
          )}
        </div>
      ) : null}
    </div>
  );
};

FilterHeader.propTypes = {
  filtersInfo: PropTypes.array.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  filtersValue: PropTypes.object,
  isFilterDropdownV2: PropTypes.bool,
  showMoreFilters: PropTypes.bool,
  splitFromIndex: PropTypes.number,
  showSearchCountLabel: PropTypes.bool,
  searchCountLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  testId: PropTypes.string,
  isFetchingMoreData: PropTypes.bool,
  hasNextPage: PropTypes.bool,
  loadMoreFeed: PropTypes.func,
  isCourseListLoading: PropTypes.bool,
  updateCourseFilterSearchText: PropTypes.func,
  isCombinedFiltersEnabled: PropTypes.bool,
};

FilterHeader.defaultProps = {
  filtersValue: {},
  isFilterDropdownV2: false,
  showMoreFilters: false,
  splitFromIndex: undefined,
  showSearchCountLabel: false,
  searchCountLabel: "",
  searchPlaceholder: undefined,
  testId: "course",
  isFetchingMoreData: false,
  hasNextPage: false,
  loadMoreFeed: () => {},
  isCourseListLoading: false,
  isCombinedFiltersEnabled: false,
};

export default FilterHeader;
